import { QuestionCircleOutlined } from '@ant-design/icons';
import {
  omitUntouchedMeasurements,
  useMeasurementUnit,
  useMeasurementUnitReady,
  useRefillOnUnitSwitch,
} from 'hooks/useMeasurementUnit';
import { withMeasurementUnitScope } from 'hooks/useMeasurementUnitScope';
import { RevxUnitSelect } from 'components/RevxUnitSelect';
import { inViewerUnit } from 'utils/measurement';
import {
  Affix,
  Divider,
  Form,
  Input,
  PageHeader,
  Popconfirm,
  Select,
  Switch,
  Tooltip,
} from 'antd';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { ArrowLeftIcon } from 'components/Icons';
import { RevxButton } from 'components/RevxButton';
import { RevxSpin } from 'components/RevxSpin';
import { Headline } from 'components/RevxTypography';
import { isPlatformOrg } from 'domains/orgDomain';
import {
  Parcel,
  ParcelCategoryEnum,
  ParcelCategoryOptions,
  PartialParcel,
} from 'domains/parcelDomain';
import { useOrgId } from 'hooks/useOrg';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ADMIN_DETAIL_AFFIX_TOP,
  PLATFORM_FORM_ITEM_LAYOUT,
} from 'utils/constants';
import { MeasureType } from 'domains/measurementDomain';
import { RevxMeasureTextInput } from 'components/RevxMeasureInput/RevxMeasureTextInput';
import {
  RULE_INTEGER,
  RULE_NUMBER,
  RULE_MEASURE_PRECISION,
  RULE_PRECISION,
  RULE_TEXT_LEN_255,
} from 'utils/validators';

type Props = {
  isBaseSelect: boolean;
};

const ParcelItemForm = ({ isBaseSelect }: Props) => {
  const { t } = useTranslation();

  const [form] = Form.useForm();

  const { unit, tag } = useMeasurementUnit();
  // Held until the org's system is known - see useMeasurementUnitReady. Opening this form in the
  // gap would label every measurement field with a unit that is about to change.
  const unitReady = useMeasurementUnitReady();
  const dispatch = useAppDispatch();
  const saving = useAppSelector(
    (state) => state.loading.effects.parcel.saveParcel
  );
  const deleting = useAppSelector(
    (state) => state.loading.effects.parcel.deleteParcel
  );
  const curItem = useAppSelector((state) => state.parcel.curItem);
  const baseSelectItem = useAppSelector(
    (state) => state.parcel.baseSelectState.curItem
  );
  const item = isBaseSelect ? baseSelectItem : curItem;

  const orgId = useOrgId();
  const isBase = isPlatformOrg({ id: orgId });

  const [isTube, setIsTube] = useState<boolean>(
    item?.category === ParcelCategoryEnum.T
  );

  const onClose = () => {
    if (isBaseSelect) {
      dispatch.parcel.updateBaseSelectItem(undefined);
      dispatch.parcel.updateBaseSelectVisible(true);
    } else {
      dispatch.parcel.setState({ curItem: undefined });
    }
  };
  const onSave = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (validationError) {
      // do nothing when form invalid
      return;
    }

    const idProp = isBaseSelect ? { id: undefined } : {};
    const baseRefProp = isBaseSelect ? { baseRefId: item?.id } : {};
    const isBaseProp = { isBase: item?.id ? isBaseSelect && isBase : isBase };
    await dispatch.parcel.saveParcel(
      tag(
        omitUntouchedMeasurements(
          { ...item, ...values, ...isBaseProp, ...baseRefProp, ...idProp },
          form
        )
      )
    );
    onClose();
  };
  const onSaveAsNew = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (validationError) {
      // do nothing when form invalid
      return;
    }
    await dispatch.parcel.saveParcel(
      tag(omitUntouchedMeasurements({ ...item, ...values, id: undefined }, form))
    );
    onClose();
  };

  const onRemove = async () => {
    if (item?.id) {
      await dispatch.parcel.deleteParcel(item);
      onClose();
    }
  };

  const [revxIdChanged, setRevxIdChanged] = useState(false);

  const onValuesChange = (changedValues: PartialParcel, allValues: Parcel) => {
    setRevxIdChanged(curItem?.revxId !== allValues.revxId);
  };

  const actionBar = (
    <div>
      <Affix offsetTop={ADMIN_DETAIL_AFFIX_TOP}>
        <PageHeader
          className="platform-item-form-header"
          onBack={() => {
            onClose();
          }}
          backIcon={<ArrowLeftIcon />}
          title={
            <Headline>
              {isBaseSelect
                ? t('platform.parcel.createParcelFromBase')
                : item?.id
                ? t('platform.parcel.form.title.edit')
                : t('platform.parcel.form.title.create')}
            </Headline>
          }
          subTitle={<RevxUnitSelect />}
          extra={[
            <Tooltip
              title={
                Boolean(curItem?.id) && revxIdChanged
                  ? t('toast.parcelRevxIdChanged')
                  : ''
              }
              placement="top"
              trigger={['hover', 'click']}
            >
              <span>
                <RevxButton
                  type="primary"
                  htmlType="submit"
                  brandGreen
                  disabled={Boolean(curItem?.id) && revxIdChanged}
                  onClick={onSave}
                >
                  {t('common.save')}
                </RevxButton>
              </span>
            </Tooltip>,
            ...(item?.id && !isBaseSelect
              ? [
                  <RevxButton onClick={onSaveAsNew} type="primary" brandGreen>
                    {t('common.saveAsNew')}
                  </RevxButton>,
                ]
              : []),

            <>
              <Divider type="vertical" />
              <RevxButton onClick={onClose} brandGreen>
                {t('common.cancel')}
              </RevxButton>
            </>,
            ...(item?.id
              ? [
                  <>
                    <Divider type="vertical" />
                    <Popconfirm
                      title={t('common.confirmQuestion')}
                      icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
                      onConfirm={onRemove}
                      okText={t('common.ok')}
                      cancelText={t('common.cancel')}
                      overlayStyle={{ width: 160 }}
                      getPopupContainer={(e) => {
                        return e as HTMLElement;
                      }}
                    >
                      <RevxButton danger>{t('common.remove')}</RevxButton>
                    </Popconfirm>
                  </>,
                ]
              : []),
          ]}
        ></PageHeader>
      </Affix>
    </div>
  );

  useRefillOnUnitSwitch(form, item);

  return (
    <RevxSpin spinning={saving || deleting || !unitReady}>
      {actionBar}
      <div className="platform-item-form-wrapper">
        <Form
          {...PLATFORM_FORM_ITEM_LAYOUT}
          className="platform-item-form"
          layout={'horizontal'}
          form={form}
          initialValues={{
            ...inViewerUnit(item, unit),
          }}
          onValuesChange={onValuesChange}
        >
          <Form.Item
            label={t('common.active')}
            name="active"
            valuePropName="checked"
          >
            <Switch disabled={!Boolean(item?.id)} />
          </Form.Item>

          <Form.Item
            label={t('common.revxId')}
            name="revxId"
            rules={[RULE_TEXT_LEN_255, { required: true }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label={t('common.name')}
            name="name"
            rules={[RULE_TEXT_LEN_255, { required: true }]}
          >
            <Input disabled={!isBase && isBaseSelect} />
          </Form.Item>
          <Form.Item
            label={t('platform.parcel.columnTitle.category')}
            name="category"
            rules={[{ required: true }]}
          >
            <Select
              disabled={!isBase && isBaseSelect}
              onChange={(v) => setIsTube(v === ParcelCategoryEnum.T)}
              getPopupContainer={(e) => {
                return e as HTMLElement;
              }}
            >
              {ParcelCategoryOptions.map((option) => {
                return (
                  <Select.Option value={option.value} key={option.value}>
                    {option.name}
                  </Select.Option>
                );
              })}
            </Select>
          </Form.Item>
          <Form.Item
            label={t('platform.parcel.columnTitle.width')}
            name="widthIn"
            rules={[RULE_MEASURE_PRECISION(3, MeasureType.LENGTH, form), { required: true }]}
          >
            <RevxMeasureTextInput
              type={MeasureType.LENGTH}
              disabled={!isBase && isBaseSelect}
            />
          </Form.Item>
          <Form.Item
            label={t('platform.parcel.columnTitle.height')}
            name="heightIn"
            rules={[RULE_MEASURE_PRECISION(3, MeasureType.LENGTH, form), { required: true }]}
          >
            <RevxMeasureTextInput
              type={MeasureType.LENGTH}
              disabled={!isBase && isBaseSelect}
            />
          </Form.Item>
          <Form.Item
            label={t('platform.parcel.columnTitle.length')}
            name="lengthIn"
            rules={[RULE_MEASURE_PRECISION(3, MeasureType.LENGTH, form), { required: true }]}
          >
            <RevxMeasureTextInput
              type={MeasureType.LENGTH}
              disabled={!isBase && isBaseSelect}
            />
          </Form.Item>
          <Form.Item
            label={t('platform.parcel.columnTitle.weight')}
            name="weightLb"
            rules={[RULE_MEASURE_PRECISION(4, MeasureType.WEIGHT, form), { required: true }]}
          >
            <RevxMeasureTextInput
              type={MeasureType.WEIGHT}
              disabled={!isBase && isBaseSelect}
            />
          </Form.Item>
          {isTube && (
            <Form.Item
              label={t('platform.parcel.columnTitle.itemsPerParcel')}
              name="itemsPerParcel"
              rules={[RULE_INTEGER]}
            >
              <Input />
            </Form.Item>
          )}
          <Form.Item
            label={t('platform.parcel.columnTitle.price')}
            name="price"
            rules={[RULE_PRECISION(2), { required: true }]}
          >
            <Input prefix="$" suffix={t('common.perPieceAbbrev')} />
          </Form.Item>
          <Form.Item
            label={t('platform.parcel.columnTitle.markup')}
            name="markupPct"
            rules={[RULE_NUMBER, { required: false }]}
          >
            <Input prefix="%" />
          </Form.Item>
        </Form>
      </div>
    </RevxSpin>
  );
};
export default withMeasurementUnitScope(ParcelItemForm);
